import type { Screen } from '../../../shared/types';
import type { ValidationIssueFamily } from './validationIssueFamilies';
import type { ValidationIssueFamilyAction } from './validationIssueRouting';
import type {
  ValidationIssueNavigationContext,
  ValidationIssueFamilyTargetPage,
  ValidationQuickFillFocus,
  ValidationWorkspaceTab,
} from '../../../shared/types/validationNavigation.types';
import { perf } from '../../../shared/lib/perfLogger';

export interface ValidationPrimaryActionNavigation {
  navigateTo?: Screen;
  requestWorkspaceTab?: ValidationWorkspaceTab;
}

export type { ValidationIssueNavigationContext } from '../../../shared/types/validationNavigation.types';

function uniqueNonEmpty(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function stripQuickFillRowDecorators(value: string): string {
  return value
    .trim()
    .replace(/\s*\(ID:?\s*[A-Za-z0-9_-]+\)\s*$/i, '')
    .replace(/\s*\[\d+\]\s*$/g, '')
    .trim();
}

function looksLikeQuickFillRowCandidate(value: string): boolean {
  const trimmed = stripQuickFillRowDecorators(value);
  if (!trimmed) {
    return false;
  }

  if (/^Element\s+\d+$/i.test(trimmed)) {
    return false;
  }

  return trimmed.includes(':') || trimmed.includes(' - ');
}

function buildQuickFillRowCandidates(family: ValidationIssueFamily): string[] {
  return uniqueNonEmpty([
    ...family.failingInstances.map((item) => item.label),
    ...family.failingTypes.flatMap((item) => item.sampleInstances),
    ...family.examples
      .map((example) => example.label)
      .filter((label) => looksLikeQuickFillRowCandidate(label)),
  ]);
}

function buildQuickFillFamilyTypeKeys(family: ValidationIssueFamily): string[] {
  return uniqueNonEmpty([
    ...family.failingInstances.map((item) => stripQuickFillRowDecorators(item.label)),
    ...family.failingTypes.flatMap((item) =>
      item.sampleInstances.map((sample) => stripQuickFillRowDecorators(sample))
    ),
    ...family.examples
      .map((example) => stripQuickFillRowDecorators(example.label))
      .filter((label) => looksLikeQuickFillRowCandidate(label)),
  ]);
}

function buildNavigationQuickFillFocus(
  family: ValidationIssueFamily
): ValidationQuickFillFocus | undefined {
  const quickFillFocus = family.primaryAction.params?.quickFillFocus;
  if (!quickFillFocus || quickFillFocus.labels.length === 0) {
    return undefined;
  }

  const resolvedFocus: ValidationQuickFillFocus = {
    ...quickFillFocus,
    requestId: `quickfill-focus-${family.id}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    rowCandidates: buildQuickFillRowCandidates(family),
    elementIds: family.impactedElementIds,
    familyTypeKeys: buildQuickFillFamilyTypeKeys(family),
    preferredRowMode: 'instance',
    autofillMode: 'overwrite',
  };

  perf.log('VAL-Handoff', 'Built Quick Fill focus payload', {
    familyId: family.id,
    validationIds: family.validationIds,
    labels: resolvedFocus.labels,
    elementIds: resolvedFocus.elementIds,
    familyTypeKeys: resolvedFocus.familyTypeKeys,
    rowCandidates: resolvedFocus.rowCandidates,
    autofillLabels: resolvedFocus.autofillLabels,
    autofillMode: resolvedFocus.autofillMode,
    suggestedCellValueCount: resolvedFocus.suggestedCellValues?.length ?? 0,
  });

  return resolvedFocus;
}

function resolveWorkspaceTabForTargetPage(
  targetPage: ValidationIssueFamilyTargetPage
): ValidationWorkspaceTab | null {
  switch (targetPage) {
    case 'mapping':
      return 'mapping';
    case 'quick-fill':
      return 'quickFill';
    case 'validation':
      return 'validation';
    default:
      return null;
  }
}

export function buildValidationIssueNavigationContext(
  currentScreen: Screen,
  family: ValidationIssueFamily
): ValidationIssueNavigationContext {
  const sourceWorkspaceTab =
    currentScreen === 'mappingConfiguration' ? 'validation' : undefined;

  const quickFillFocus =
    family.targetPage === 'quick-fill' ? buildNavigationQuickFillFocus(family) : undefined;
  if (family.targetPage === 'quick-fill' && !quickFillFocus) {
    perf.warn('VAL-Handoff', 'Quick Fill navigation context missing focus payload', {
      familyId: family.id,
      title: family.title,
      affectedItems: family.affectedItems,
      targetLabel: family.targetLabel,
      primaryActionLabel: family.primaryAction.label,
      primaryActionRoute: family.primaryAction.route || null,
      validationIds: family.validationIds,
    });
  }
  const actionParams =
    family.primaryAction.params || quickFillFocus
      ? {
          ...family.primaryAction.params,
          ...(quickFillFocus ? { quickFillFocus } : {}),
        }
      : undefined;

  const navigationContext: ValidationIssueNavigationContext = {
    familyId: family.id,
    title: family.title,
    summary: family.summary,
    severity: family.severity,
    category: family.category,
    targetPage: family.targetPage,
    targetLabel: family.targetLabel,
    affectedCount: family.affectedCount,
    affectedItems: family.affectedItems,
    actionParams,
    ...(quickFillFocus ? { quickFillFocus } : {}),
    validationIds: family.validationIds,
    sourceScreen: currentScreen,
    sourceWorkspaceTab,
  };

  if (quickFillFocus) {
    perf.log('VAL-Handoff', 'Built Validation navigation context', {
      familyId: navigationContext.familyId,
      sourceScreen: currentScreen,
      targetPage: family.targetPage,
      requestId: quickFillFocus.requestId,
      labels: quickFillFocus.labels,
      elementIds: quickFillFocus.elementIds,
      familyTypeKeys: quickFillFocus.familyTypeKeys,
    });
  }

  return navigationContext;
}

export function resolveValidationPrimaryActionNavigation(
  currentScreen: Screen,
  action: ValidationIssueFamilyAction,
  targetPage: ValidationIssueFamilyTargetPage
): ValidationPrimaryActionNavigation {
  if (!action.route) {
    return {};
  }

  if (
    currentScreen === 'mappingConfiguration' &&
    ['mappingConfiguration', 'quickFillProperties', 'pcfValidation'].includes(action.route)
  ) {
    const requestedTab = resolveWorkspaceTabForTargetPage(targetPage);
    if (requestedTab) {
      return {
        requestWorkspaceTab: requestedTab,
      };
    }
  }

  if (
    currentScreen === 'pcfValidation' &&
    targetPage === 'quick-fill' &&
    action.route === 'quickFillProperties'
  ) {
    return {
      navigateTo: 'mappingConfiguration',
      requestWorkspaceTab: 'quickFill',
    };
  }

  if (action.route === 'mappingConfiguration') {
    const requestedTab = resolveWorkspaceTabForTargetPage(targetPage);
    return {
      navigateTo: 'mappingConfiguration',
      requestWorkspaceTab: requestedTab || 'mapping',
    };
  }

  return {
    navigateTo: action.route,
  };
}
